import {
  useAuthenticationContext,
  useCurrentAccountId,
} from "@/store/authContext/authContext";
import { useEffect, useRef, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step, Styles } from "react-joyride";
import { setUserOnboarded } from "@/lib/serverComm";
import { PARITA_COMMUNITY_DOMAIN } from "@/constants/user";
// import { useLocation } from "react-router-dom";

const Onboarding = () => {
  const { state: authContext, dispatch } = useAuthenticationContext();
  const currentAccountId = useCurrentAccountId();
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [runOnboarding, setRunOnboarding] = useState(false);
  const [onboarded, setOnboarded] = useState(true); // Default to true before recieving user data.
  useEffect(() => {
    alertTimeoutRef.current = setTimeout(() => {
      if (!onboarded && currentAccountId === PARITA_COMMUNITY_DOMAIN) {
        setRunOnboarding(true);
      }
    }, 1000);

    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, [onboarded, currentAccountId]);

  useEffect(() => {
    if (authContext.userData) {
      setOnboarded(authContext.userData.onboarded);
    }
  }, [authContext.userData]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      dispatch({ type: "updateUserOnboardingAction", payload: true });
      setUserOnboarded();
      setRunOnboarding(false);
    }
  };

  // Custom styles to match Parita design system
  const joyrideStyles: Styles = {
    options: {
      primaryColor: "hsl(206 100% 50%)", // Parita sea-500 blue
      textColor: "hsl(240 10% 3.9%)", // foreground color
      backgroundColor: "hsl(0 0% 100%)", // background color
      arrowColor: "hsl(0 0% 100%)",
      overlayColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 10000,
    },
    tooltip: {
      fontFamily: '"Lato", "DM Sans", sans-serif',
      fontSize: 14,
      padding: 20,
      borderRadius: 8,
    },
    tooltipTitle: {
      fontSize: 18,
      fontWeight: 600,
      marginBottom: 8,
      color: "hsl(240 10% 3.9%)",
    },
    tooltipContent: {
      fontSize: 14,
      lineHeight: 1.6,
      color: "hsl(240 3.8% 46.1%)", // muted-foreground
    },
    buttonNext: {
      backgroundColor: "hsl(206 100% 50%)", // sea-500
      fontSize: 14,
      fontWeight: 500,
      borderRadius: 6,
      padding: "10px 16px",
      transition: "all 0.2s",
    },
    buttonBack: {
      color: "hsl(206 100% 50%)", // sea-500
      fontSize: 14,
      fontWeight: 500,
      marginRight: 8,
    },
    buttonSkip: {
      color: "hsl(240 3.8% 46.1%)", // muted-foreground
      fontSize: 14,
    },
    buttonClose: {
      padding: 8,
    },
  } as Styles;

  const steps: Step[] = [
    {
      target: "#query-input",
      title: "Ask Questions",
      content:
        "Enter a workforce question. Get a data-backed answer instantly. See example questions below!",
      disableBeacon: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#new-report-button",
      title: "Create Custom Reports",
      content:
        "Tailor AI-generated reports or build your own from scratch to analyze key workforce metrics.",
      disableBeacon: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#sidebar-decks-button",
      title: "Build Presentation Decks",
      content:
        "Generate presentation-ready slides based on your reports and insights.",
      disableBeacon: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#sidebar-smart-workspaces",
      title: "Dive In With Workspaces",
      content:
        "Monitor important KPIs, trends and risks in customizable dashboards.",
      disableBeacon: true,
      showProgress: true,
      showSkipButton: true,
    },
  ];
  return (
    <Joyride
      run={runOnboarding}
      continuous
      steps={steps}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      locale={{
        back: "Back",
        close: "Close",
        last: "Done",
        next: "Next",
        skip: "Skip tour",
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
};

export default Onboarding;
